import React from 'react';
import _merge from 'lodash/merge';
import useMeasure from 'react-use-measure';
import { isFirefox } from 'react-device-detect';
import ResizeObserver from 'resize-observer-polyfill';
import ResizeHandleIcon from '@nokia-csf-uxr/ccfk-assets/ResizeHandleIcon';
import CloseIcon from '@nokia-csf-uxr/ccfk-assets/CloseIcon';
import { SPACING_4, SPACING_16 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';

import { getContext, useCombinedRefs } from 'common';
import IconButton from 'components/IconButton';
import { TextAreaErrorMessage } from 'components/TextArea';


import Styled from './styles';

export interface TextwChipsCoreProps extends React.ComponentPropsWithRef<'div'> {
  /** Set `true`, to render in focused visual. */
  focused?: boolean;
  /** The contents  including `<Chip />`, `<TextInput />` */
  children: React.ReactNode;
  /** Properties to apply to the children wrapper element. For example add a style object to set the initial height. */
  wrapperProps?: React.ComponentPropsWithRef<any>;
  /** Properties apply to the component wrapper. */
  wholeWrapperProps?: {};
  /** If `true`, this component will have a clear button. */
  showClearButton?: boolean;
  /** Properties for the clear button. */
  clearButtonProps?: {};
  /** Render a custom icon (to replace the default clear icon). See <IconButton /> and <Icon/> component for more information. */
  clearButtonIcon?: React.ReactNode;
  /** If true, the component is empty. */
  isEmpty?: boolean;
  /** If true, resize is allowed. */
  resize?: boolean;
  /** If `true`, the input will be rendered in the error state. (Also, input can be in error state if `errorMessage` is set.) */
  error?: boolean;
  /** If set, the message will be displayed in the error state. `Typography` component can be used. */
  errorMessage?: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** If `true`, the component will be disabled. */
  disabled?: boolean;
  /** If `true`, the field cannot be edited by the user. */
  readOnly?: boolean;
  /** Display the component with an outline or underline style.  */
  variant?: 'outlined' | 'underlined';
  /** If true, allow the component to display more than one row. */
  multiline?: boolean;
  /**
   * To override the RTL internally
   * @ignore
   */
  RTL?: boolean;
}

/** Internal component. Any other props than the `TextwChipsCore` props will be provided to the root element. */
const TextwChipsCore = React.forwardRef<HTMLElement, TextwChipsCoreProps>((props: TextwChipsCoreProps, ref) => getContext(({ RTL, theme }) => {
  const {
    children,
    wrapperProps,
    theme: propTheme,
    showClearButton,
    clearButtonIcon,
    clearButtonProps,
    isEmpty,
    resize,
    error,
    errorMessage,
    wholeWrapperProps,
    ...otherProps
  } = props;
  const { variant, disabled, readOnly, multiline, ...restOfOtherProps } = otherProps;
  const [fieldEl, { width: fieldWidth }] = useMeasure({ polyfill: ResizeObserver });
  const combinedRef = useCombinedRefs(ref, fieldEl);
  const coreProps = {
    variant,
    disabled,
    readOnly,
    multiline,
    hasClearButton: showClearButton,
  };
  const clearButtonPosition = () => {
    if (!coreProps.multiline) {
      return ({
        position: 'relative' as const,
        alignSelf: 'center',
        left: RTL ? SPACING_4 : undefined,
        marginRight: RTL ? 'auto' : undefined,
        right: !RTL ? SPACING_4 : undefined,
        marginLeft: !RTL ? 'auto' : undefined,
      });
    }
    return ({
      position: 'absolute' as const,
      display: fieldWidth === 0 ? 'none' : 'block',
      right: RTL ? `${(fieldWidth - 24) / 16}rem` : undefined,
      left: !RTL ? `${(fieldWidth - 24) / 16}rem` : undefined,
      height: '1.25rem',
      top: SPACING_4,
    });
  };
  const resizeIconStyle = _merge(
    {},
    {
      position: 'absolute' as const,
      bottom: SPACING_4,
      pointerEvents: 'none' as const,
      display: fieldWidth === 0 ? 'none' : 'block'
    },
    RTL ? { right: `${(fieldWidth - 20) / 16}rem` } : { left: `${(fieldWidth - 20) / 16}rem` }
  );
  const resolvedTheme = _merge({}, variant === 'outlined' ?
    theme.TEXT_INPUT.OUTLINED :
    theme.TEXT_INPUT.UNDERLINED, propTheme);

  return (
    <Styled.WholeWrapper {...wholeWrapperProps}> {/* for error message positioning */}
      <Styled.TextwChipsCore ref={combinedRef} RTL={RTL} error={error} theme={resolvedTheme} {...coreProps} disabled={disabled} {...restOfOtherProps}>
        <Styled.ContentWrapper tabIndex={-1} multiline={coreProps.multiline} {...wrapperProps}>
          {children}
        </Styled.ContentWrapper>
        {!isEmpty && !readOnly && showClearButton &&
          (
            <IconButton
              size="small"
              rippleStyle={clearButtonPosition()}
              disabled={disabled}
              {...clearButtonProps}
            >
              { clearButtonIcon || <CloseIcon /> }
            </IconButton>
          )
        }
        {resize && !isFirefox && (
          <ResizeHandleIcon
            size={{ width: SPACING_16, height: SPACING_16 }}
            style={resizeIconStyle}
          />
        )}
      </Styled.TextwChipsCore>
      <Styled.MetaContainer $rtl={RTL} fieldWidth={fieldWidth}>
        {error && (typeof errorMessage === 'string' ? <TextAreaErrorMessage>{errorMessage}</TextAreaErrorMessage> : errorMessage)}
      </Styled.MetaContainer>
    </Styled.WholeWrapper>
  );
}));

TextwChipsCore.displayName = 'TextwChipsCore';

TextwChipsCore.defaultProps = {
  focused: false,
  wrapperProps: {},
  wholeWrapperProps: {},
  theme: {},
  showClearButton: false,
  clearButtonProps: {},
  clearButtonIcon: undefined,
  isEmpty: false,
  resize: false,
  error: false,
  errorMessage: undefined,
  RTL: undefined,
  variant: 'outlined',
  multiline: false,
  disabled: false,
  readOnly: false
};

export default TextwChipsCore;
